import { yupResolver } from '@hookform/resolvers/yup'
import { Avatar, Box, IconButton } from '@mui/material'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomDialog from 'components/dialogs/CustomDialog'
import { useGetUserById, useUpdateProfile, useUpdateProfilePicture } from 'hooks/useUsers'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { snackbarService } from 'services/snackbarService'
import * as yup from 'yup';
import { useAuth } from 'context/AuthContext'
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect'
import { useGetSpecialities, useGetUserRoles } from 'hooks/useLookups'
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput'
import * as usersService from 'services/usersService';
import { PhotoCamera } from '@mui/icons-material'
import { colors } from 'styles/colors'
import ChangePasswordDialog from '../ChangePasswordDialog/ChangePasswordDialog'

const validationSchema = yup.object({
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  role_id: yup.string().required(),
  speciality_id: yup.string().required(),
  email: yup.string().email().required(),
})

interface Props {
  open: boolean,
  setOpen: (value: boolean) => void;
  handleClose: () => void;
}

export default function EditProfileDialog({
  open,
  setOpen,
  handleClose,
}: Props) {
  const { user, login } = useAuth();
  const { data: specialities } = useGetSpecialities();
  const { data: roles } = useGetUserRoles();
  const [passwordOpen, setPasswordOpen] = useState<boolean>(false);
  const [imagePath, setImagePath] = useState<File | null>(null);

  const { data: fetchedUser, isFetching } = useGetUserById(user.id, {
    onSuccess: (data: any) => {
      reset({
        first_name: data.first_name,
        last_name: data.last_name,
        role_id: data?.user_role?.id?.toString() || '',
        speciality_id: data?.user_profile[0]?.speciality?.id.toString(),
        email: data.email
      });
    }
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      role_id: '',
      speciality_id: '',
    }
  });

  const {
    handleSubmit,
    formState: { isValid },
    reset,
    register,
  } = methods;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImagePath(file);
  };

  const { mutateAsync: updateProfilePictureMutation } = useUpdateProfilePicture(
    () => { },
    () => { },
  );

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateProfile(
    async (data: any) => {

      if (imagePath) {
        await updateProfilePictureMutation({
          userId: user.id,
          image: imagePath
        });
      }

      const updatedUser = await usersService.getUserById(user.id);

      login((prev: any) => ({
        ...prev,
        ...updatedUser,
      }));

      onClose();

      snackbarService.showSnackbar('Profile has been updated successfully', 'success');
    },
    (error: any) => {
      snackbarService.showSnackbar(error.message, 'error');
    }
  )

  const onSubmit = (data: any) => {
    updateMutation({
      userId: user.id,
      dto: data,
    });
  };

  const onClose = () => {
    handleClose();
    setOpen(false);
  }

  const openChangePassword = () => {
    setPasswordOpen(true);
  }

  return (
    <>
      <CustomDialog
        open={open}
        title={`Edit personal info`}
        alignText='center'
        onClose={onClose}
        content={
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
            >

              {/* Avatar */}
              <Box display="flex" justifyContent="center" mt={2}>
                <Box position="relative">

                  <input
                    id="profile-image"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />

                  <Avatar
                    src={
                      imagePath
                        ? URL.createObjectURL(imagePath)
                        : user?.profile_image?.href_small || undefined
                    }
                    sx={{
                      width: 100,
                      height: 100,
                      cursor: 'pointer',
                      bgcolor: colors.avatarBg,
                    }}
                    onClick={() =>
                      document.getElementById('profile-image')?.click()
                    }
                  />

                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: 'white',
                      boxShadow: 1,
                      '&:hover': { backgroundColor: 'grey.200' }
                    }}
                    onClick={() =>
                      document.getElementById('profile-image')?.click()
                    }
                  >
                    <PhotoCamera fontSize="small" />
                  </IconButton>

                </Box>
              </Box>

              {/* First name */}
              <Box mt={3}>
                <CustomFormTextInput
                  name={'first_name'}
                  register={register}
                  placeholder='First name'
                  floatingLabel='First name'
                  required
                />
              </Box>

              {/* Last name */}
              <Box mt={2}>
                <CustomFormTextInput
                  name={'last_name'}
                  register={register}
                  placeholder='Last name'
                  floatingLabel='Last name'
                  required
                />
              </Box>

              {/* Role */}
              <Box mt={2}>
                <CustomFormSelect
                  name={'role_id'}
                  options={roles || []}
                  floatingLabel='Role'
                  placeholder='Select role'
                  required
                />
              </Box>

              {/* Speciality */}
              <Box mt={2}>
                <CustomFormSelect
                  name={'speciality_id'}
                  options={specialities || []}
                  floatingLabel='Speciality'
                  placeholder='Select speciality'
                  required
                />
              </Box>

              {/* Email */}
              <Box mt={2}>
                <CustomFormTextInput
                  name={'email'}
                  register={register}
                  placeholder='Email'
                  floatingLabel='Email'
                  required
                />
              </Box>

              {/* Password */}
              <Box
                mt={2}
                onClick={openChangePassword}
              >
                <CustomFormTextInput
                  name={'change_password'}
                  register={register}
                  placeholder='Change your password from here'
                  floatingLabel='Password'
                  editable={false}
                />
              </Box>

              <Box my={2}>
                <CustomButton
                  text={'Save'}
                  color='primary'
                  fullWidth
                  type='submit'
                  size='large'
                  loading={isUpdating}
                  disabled={!isValid}
                />
              </Box>

            </Box>
          </FormProvider>
        }
      />

      <ChangePasswordDialog 
        open={passwordOpen}
        setOpen={setPasswordOpen}
      />
    </>
  )
}
